/**
 * Client-Side Media Optimizer & Compression Engine
 * Meresize dan mengompres foto langsung di browser pengguna menggunakan HTML5 Canvas
 * Mengubah foto 5MB - 10MB menjadi hanya 30KB - 80KB WebP/JPEG berkualitas tinggi,
 * menjaga database SQLite / SQL backup tetap super ringan & hemat kuota.
 */

export interface CompressionResult {
  dataUrl: string;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  width: number;
  height: number;
}

/**
 * Kompresi gambar File atau Blob ke format Data URL (WebP / JPEG)
 */
export async function compressImageFile(
  file: File | Blob,
  maxWidth = 1200,
  maxHeight = 900,
  quality = 0.78
): Promise<CompressionResult> {
  const originalSize = file.size;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;

      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Hitung aspect ratio scaling
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context tidak didukung'));
          return;
        }

        // Gambar dengan smoothing berkualitas tinggi
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        // Coba konversi ke WebP, fallback ke JPEG jika browser tidak mendukung
        let mimeType = 'image/webp';
        let compressedDataUrl = canvas.toDataURL(mimeType, quality);

        if (!compressedDataUrl.startsWith('data:image/webp')) {
          mimeType = 'image/jpeg';
          compressedDataUrl = canvas.toDataURL(mimeType, quality);
        }

        // Hitung estimasi ukuran byte dari Base64
        const head = `data:${mimeType};base64,`;
        const base64Str = compressedDataUrl.substring(head.length);
        const compressedSize = Math.round((base64Str.length * 3) / 4);
        const compressionRatio = Math.round(((originalSize - compressedSize) / originalSize) * 100);

        resolve({
          dataUrl: compressedDataUrl,
          originalSize,
          compressedSize,
          compressionRatio: Math.max(0, compressionRatio),
          width,
          height,
        });
      };

      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
}

/**
 * Alias helper untuk kompresi cepat
 */
export async function compressImageToWebP(
  file: File | Blob,
  maxWidth = 1200,
  quality = 0.82
): Promise<{ dataUrl: string; originalSizeBytes: number; compressedSizeBytes: number; width: number; height: number }> {
  const res = await compressImageFile(file, maxWidth, 900, quality);
  return {
    dataUrl: res.dataUrl,
    originalSizeBytes: res.originalSize,
    compressedSizeBytes: res.compressedSize,
    width: res.width,
    height: res.height,
  };
}

export const formatFileSize = formatBytes;

export function formatBytes(bytes: number, decimals = 1): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Helper untuk membaca video lokal dari komputer dengan batas ukuran aman
 */
export async function readVideoFile(
  file: File,
  maxSizeMB = 20
): Promise<{ dataUrl: string; size: number; duration?: number }> {
  if (file.size > maxSizeMB * 1024 * 1024) {
    throw new Error(
      `Ukuran video terlalu besar (${formatBytes(file.size)}). Batas maksimal video langsung adalah ${maxSizeMB}MB agar backup database tetap ringan. Gunakan link YouTube jika video berdurasi panjang.`
    );
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      resolve({
        dataUrl: e.target?.result as string,
        size: file.size,
      });
    };
    reader.onerror = (err) => reject(err);
  });
}
